import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { documentTypeLabel, useDocumentTypes } from "@/api/documentTypes";
import {
  useAttestDocumentRequest,
  useEscalateDocumentRequest,
  useUpdateDocumentRequestNotes,
} from "@/hooks/deals/useDealDetail";
import {
  TrackedItemRow,
  DOC_REQUEST_STATE_SET,
} from "@/components/DealFlow/shared/TrackedItemRow";
import { AddCustomDocumentRequestModal } from "../DocumentRequestModals";
import { DocumentRequestPopup } from "../DocumentRequestPopup";
import {
  DOCUMENT_REQUEST_PERMISSIONS,
  type DealDetailDto,
  type DocumentRequestAttestStatus,
  type PreApprovalDocumentRequestDto,
  type PreApprovalRequestStatus,
} from "@/types/deals";
import { formatCalendarDate, useUserTimeZone } from "@/utils/datetime";
// `isPastCalendarDay` comes straight from the shared source of truth, which is
// where the rule lives (`@/utils/datetime` also re-exports it).
import { isPastCalendarDay } from "@/design-system/lib/format";

interface Props {
  deal: DealDetailDto;
  requests: PreApprovalDocumentRequestDto[];
}

type Filter = "all" | "required" | "optional" | "overdue" | "pending-review" | "approved";

// Reject / Waive need a ReasonCode, so picking those targets opens the
// existing rich attestation popup instead of a direct state change.
const REASON_CODED_TARGETS: PreApprovalRequestStatus[] = ["Rejected", "Waived"];

/**
 * A request is overdue once its DUE DAY has fully passed in the viewer's
 * zone — never merely because the stored instant is behind the clock.
 *
 * `dueDate` is a wall-calendar `DateOnly` widened server-side to UTC
 * midnight, so the shape this replaces (`new Date(req.dueDate) < new Date()`)
 * flipped true at 17:00 Phoenix the day BEFORE the due day and stayed true
 * through it: a request sat in the Overdue filter and the Overdue chip for 31
 * continuous hours, including the entire day it was actually due. Anchoring
 * both sides to their own calendar day removes the time of day from the
 * comparison — see `isPastCalendarDay`.
 *
 * `now` is injectable so the boundary can be pinned in a test without
 * freezing the clock.
 */
export function isOverdue(
  req: PreApprovalDocumentRequestDto,
  now: Date = new Date(),
): boolean {
  if (
    req.status === "Approved" ||
    req.status === "Waived" ||
    req.status === "Rejected" ||
    req.status === "Escalated"
  ) {
    return false;
  }
  return isPastCalendarDay(req.dueDate, now);
}

export function DocumentRequestsSection({ deal, requests }: Props) {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const tz = useUserTimeZone();
  // Admin-authored escalation guidance from the live DocumentTypeRef catalog;
  // falls back to a computed string per request when blank/absent.
  const { data: documentTypes } = useDocumentTypes();
  const escalationByCode = useMemo(
    () => new Map(documentTypes.map((d) => [d.code, d.defaultEscalationPath ?? null])),
    [documentTypes],
  );
  const attest = useAttestDocumentRequest(deal.id);
  const escalate = useEscalateDocumentRequest(deal.id);
  const notesMutation = useUpdateDocumentRequestNotes(deal.id);

  const canCreate = hasPermission(DOCUMENT_REQUEST_PERMISSIONS.Create);
  const canUpdate = hasPermission(DOCUMENT_REQUEST_PERMISSIONS.Update);
  const canEscalate = hasPermission(DOCUMENT_REQUEST_PERMISSIONS.Escalate);

  const [filter, setFilter] = useState<Filter>("all");
  const [addOpen, setAddOpen] = useState(false);
  const [popupTarget, setPopupTarget] = useState<PreApprovalDocumentRequestDto | null>(null);

  const filtered = useMemo(() => {
    switch (filter) {
      case "required":
        return requests.filter((r) => r.required);
      case "optional":
        return requests.filter((r) => !r.required);
      case "overdue":
        // Arrow, not a bare `isOverdue` reference: `Array.prototype.filter`
        // passes (value, index, array), so the index would land in `now`.
        return requests.filter((r) => isOverdue(r));
      case "pending-review":
        return requests.filter((r) => r.status === "Received" || r.status === "UnderReview");
      case "approved":
        return requests.filter((r) => r.status === "Approved");
      default:
        return requests;
    }
  }, [filter, requests, tz]);

  async function changeState(req: PreApprovalDocumentRequestDto, next: string) {
    // Reject / Waive carry a required ReasonCode — route those through the
    // existing attestation popup. Everything else is a direct transition.
    if (REASON_CODED_TARGETS.includes(next as PreApprovalRequestStatus)) {
      setPopupTarget(req);
      return;
    }
    try {
      await attest.mutateAsync({
        id: req.id,
        body: {
          targetStatus: next as DocumentRequestAttestStatus,
          notes: null,
          receivedDate: null,
          receivedFromContact: null,
          reasonCode: null,
        },
      });
      toast.success(`${documentTypeLabel(req.documentTypeRefCode)} moved to ${next}.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function escalateRequest(req: PreApprovalDocumentRequestDto, reason: string) {
    try {
      await escalate.mutateAsync({ id: req.id, reason });
      toast.success(`${documentTypeLabel(req.documentTypeRefCode)} escalated.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function saveNotes(req: PreApprovalDocumentRequestDto, notes: string) {
    try {
      await notesMutation.mutateAsync({ id: req.id, notes });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-space-3 flex-wrap gap-space-2">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">Document requests</div>
          <p className="text-small text-gray-mid m-0">
            Work each request as you gather documents; open a row for full
            context or to escalate.
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          {canCreate && (
            <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>
              <Plus size={14} aria-hidden /> Add custom request
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-space-2 flex-wrap mb-space-3">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
          All ({requests.length})
        </FilterChip>
        <FilterChip active={filter === "required"} onClick={() => setFilter("required")}>
          Required
        </FilterChip>
        <FilterChip active={filter === "optional"} onClick={() => setFilter("optional")}>
          Optional
        </FilterChip>
        <FilterChip active={filter === "overdue"} onClick={() => setFilter("overdue")}>
          Overdue ({requests.filter((r) => isOverdue(r)).length})
        </FilterChip>
        <FilterChip
          active={filter === "pending-review"}
          onClick={() => setFilter("pending-review")}
        >
          Received
        </FilterChip>
        <FilterChip active={filter === "approved"} onClick={() => setFilter("approved")}>
          Approved
        </FilterChip>
      </div>

      {filtered.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No document requests match this filter.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
          {filtered.map((req) => (
            <TrackedItemRow
              key={req.id}
              title={documentTypeLabel(req.documentTypeRefCode)}
              description={req.required ? "Required document." : "Optional document."}
              escalationPath={
                escalationByCode.get(req.documentTypeRefCode)?.trim() ||
                `Escalation routes to the Loan Administrator. Counterparty: ${req.requestedFromParty}.`
              }
              metadata={[
                { label: "Party", value: req.requestedFromParty },
                { label: "Due", value: formatCalendarDate(req.dueDate) },
                { label: "Requirement", value: req.required ? "Required" : "Optional" },
              ]}
              state={req.status}
              stateSet={DOC_REQUEST_STATE_SET}
              onStateChange={(next) => changeState(req, next)}
              stateChangePending={attest.isPending}
              notes={req.notes ?? ""}
              onNotesChange={(notes) => saveNotes(req, notes)}
              notesSavePending={notesMutation.isPending}
              canEscalate={canEscalate && canUpdate}
              escalationStatus={req.escalation?.status ?? "None"}
              escalationDirective={req.escalation?.resolutionDirective}
              escalationResolvedAt={req.escalation?.resolvedAt}
              onEscalate={(reason) => escalateRequest(req, reason)}
              escalatePending={escalate.isPending}
              isReadOnly={!canUpdate}
            />
          ))}
        </ul>
      )}

      <AddCustomDocumentRequestModal
        open={addOpen}
        dealId={deal.id}
        onClose={() => setAddOpen(false)}
      />
      <DocumentRequestPopup
        open={!!popupTarget}
        dealId={deal.id}
        request={popupTarget}
        onClose={() => setPopupTarget(null)}
      />
    </Card>
  );
}

function FilterChip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-space-3 py-[6px] rounded-pill border text-caption uppercase tracking-label transition-colors ${
        active
          ? "bg-onyx text-white-pure border-onyx"
          : "bg-white-pure text-gray-mid border-border-light hover:text-onyx"
      }`}
    >
      {children}
    </button>
  );
}
